/**
 * Suppliers API (owner: C2): the supplier directory, catalogs with offers in the workspace market and
 * currency, price history, listing matches, shipping rules, "sync now" and supplier continuity under
 * /api/suppliers/ (backend/suppliers/api.py). Money is integer minor units plus a currency; prices per
 * neat gram are decimal strings in minor units. Query keys start with "suppliers".
 */
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/api/client";
import type { DecimalString, IsoDateTime, ListParams, MinorUnits, Paged, Uuid } from "@/api/types";
import { formatNumber, formatPercentNumber } from "@/lib/format";

export type SupplierPlatform =
  "shopify" | "woocommerce" | "bigcommerce" | "magento" | "squarespace" | "wix" | "custom" | "marketplace";
export type SupplierStatus = "active" | "defunct" | "blocked";

export interface SyncRun {
  id: Uuid;
  kind: "catalog" | "cas_backfill" | "prices" | "ucp_quote";
  market: string;
  status: "running" | "done" | "partial" | "failed";
  started_at: IsoDateTime;
  finished_at: IsoDateTime | null;
  error: string;
  job_id: Uuid | null;
  products_seen: number | null;
  variants_seen: number | null;
  observations_appended: number | null;
  /** Every catalog page came back unchanged (mostly 304 Not Modified), so nothing was parsed. */
  catalog_unchanged: boolean;
  downloaded_kb: number | null;
}

export interface Supplier {
  id: Uuid;
  slug: string;
  name: string;
  url: string;
  country: string;
  platform: SupplierPlatform;
  currency: string;
  ships_to: string[];
  /** Ships to the workspace's ship-to country; null when the directory doesn't say. */
  ships_to_workspace: boolean | null;
  scraping_difficulty: number | null;
  ucp: boolean;
  feed_url: string;
  has_open_feed: boolean;
  /** A catalog adapter exists (Shopify, WooCommerce). */
  syncable: boolean;
  sync_enabled: boolean;
  /** Learned time between catalog checks: shorter when checks find changes, longer when they don't. */
  sync_interval_hours: number | null;
  /** When the next catalog check is due; null = within the next few minutes. */
  next_sync_at: IsoDateTime | null;
  /** The last check that found new prices, stock or listings. */
  last_changed_at: IsoDateTime | null;
  status: SupplierStatus;
  product_count: number | null;
  last_sync: SyncRun | null;
}

export interface PolicyVersion {
  id: Uuid;
  policy: Record<string, unknown>;
  source_url: string;
  verified_at: IsoDateTime | null;
  notes: string;
  created_at: IsoDateTime;
}

export interface ShippingRule {
  id: Uuid;
  supplier_id: Uuid;
  supplier_name: string;
  flat_fee_minor: MinorUnits;
  free_threshold_minor: MinorUnits | null;
  dg_fee_minor: MinorUnits;
  currency: string;
  /** Duty and import tax as a fraction of the goods value ("0.12" = 12 %). */
  duty_fraction: DecimalString;
  brokerage_minor: MinorUnits;
  notes: string;
  updated_at: IsoDateTime;
}

/** A rule in the workspace currency: George's own ("workspace") or derived from the published policy. */
export interface RuleView {
  source: "workspace" | "policy";
  currency: string;
  flat_fee_minor: MinorUnits;
  free_threshold_minor: MinorUnits | null;
  dg_fee_minor: MinorUnits;
  duty_fraction: DecimalString;
  brokerage_minor: MinorUnits;
  min_order_minor: MinorUnits;
  max_order_minor: MinorUnits | null;
  ddp: boolean;
  shipping_known: boolean;
  notes: string[];
  rule_id: string | null;
  policy_id: string | null;
  policy_source_url: string;
}

export interface SupplierShipping {
  rule: ShippingRule | null;
  default: RuleView;
  effective: RuleView;
  ship_to_country: string;
  currency: string;
}

export interface CatalogStats {
  products: number;
  active_products: number;
  variants: number;
  active_variants: number;
  in_stock_variants: number;
  matched_products: number;
  accepted_matches: number;
  proposed_matches: number;
  rejected_matches: number;
  last_observed_at: IsoDateTime | null;
  markets: string[];
}

export interface SupplierDetail extends Supplier {
  notes: string;
  organization_id: Uuid | null;
  directory: Record<string, string>;
  policy_versions: PolicyVersion[];
  shipping: SupplierShipping;
  catalog: CatalogStats;
  sync_runs: SyncRun[];
}

export interface Offer {
  /** In the workspace currency. */
  price_minor: MinorUnits | null;
  currency: string;
  pack_neat_g: DecimalString | null;
  /** Minor units of the workspace currency per neat gram. */
  per_neat_g_minor: DecimalString | null;
  in_stock: boolean | null;
  listed_price_minor: MinorUnits | null;
  listed_currency: string;
  listed_compare_at_minor: MinorUnits | null;
  market: string;
  observed_at: IsoDateTime | null;
  notes: string[];
}

export interface Variant {
  id: Uuid;
  external_id: string;
  title: string;
  sku: string;
  pack_qty: DecimalString | null;
  pack_unit: "g" | "ml" | "oz" | "unit" | null;
  pack_g: DecimalString | null;
  /** Active fraction of a pre-diluted variant ("0.1" = 10 %), null when neat. */
  dilution_fraction: DecimalString | null;
  solvent_text: string;
  purity_note: string;
  active: boolean;
  offer: Offer;
}

export type MatchStatus = "proposed" | "accepted" | "rejected";
export type MatchMethod = "cas" | "trade_name" | "fuzzy" | "scentree_code" | "manual";

export interface MatchRef {
  id: Uuid;
  material_id: Uuid;
  material_name: string;
  method: MatchMethod;
  review_status: MatchStatus;
  confidence: DecimalString | null;
}

export interface Product {
  id: Uuid;
  supplier_id: Uuid;
  title: string;
  handle: string;
  url: string;
  vendor: string;
  product_type: string;
  cas: string | null;
  active: boolean;
  first_seen: IsoDateTime | null;
  last_seen: IsoDateTime | null;
  matches: MatchRef[];
  variants: Variant[];
  best_per_neat_g_minor: DecimalString | null;
}

export interface ProductListParams extends ListParams {
  in_stock?: boolean;
  matched?: boolean;
}

export interface HistoryPoint {
  observed_at: IsoDateTime;
  price_minor: MinorUnits;
  currency: string;
  compare_at_minor: MinorUnits | null;
  in_stock: boolean;
  /** At today's exchange rate, in the workspace currency. */
  converted_minor: MinorUnits | null;
}

export interface VariantHistory {
  variant: { id: Uuid; title: string; pack_g: DecimalString | null; dilution_fraction: DecimalString | null };
  product: { id: Uuid; title: string; url: string };
  supplier: { id: Uuid; name: string };
  currency: string;
  market: string;
  series: Array<{ market: string; currency: string; points: HistoryPoint[] }>;
  notes: string[];
}

export interface ListingMatch {
  id: Uuid;
  product: { id: Uuid; title: string; cas: string | null; url: string; supplier_id: Uuid; supplier_name: string };
  material: { id: Uuid; name: string };
  method: MatchMethod;
  confidence: DecimalString | null;
  review_status: MatchStatus;
  updated_at: IsoDateTime;
}

export interface MatchListParams extends ListParams {
  status?: MatchStatus;
  supplier_id?: Uuid;
  material_id?: Uuid;
}

export interface SupplierListParams extends ListParams {
  ships_to_me?: boolean;
  open_feed?: boolean;
  status?: SupplierStatus;
}

export interface ContinuityAlternative {
  supplier: { id: Uuid; slug: string; name: string };
  product_id: Uuid;
  product_title: string;
  variant_id: Uuid;
  variant_title: string;
  url: string;
  price_minor: MinorUnits | null;
  currency: string;
  per_neat_g_minor: DecimalString;
  match_status: MatchStatus;
  /** Set when the alternative is another material with the same substance. */
  via_material: string | null;
}

export interface ContinuityEntry {
  supplier: { id: Uuid; slug: string; name: string; status: SupplierStatus; sync_enabled: boolean; url: string };
  reason: "defunct" | "blocked" | "stale" | "sync_failing";
  last_success_at: IsoDateTime | null;
  last_data_at: IsoDateTime | null;
  last_error: string;
  affected: Array<{
    material_id: Uuid;
    material_name: string;
    owned_g: DecimalString | null;
    reasons: Array<"lot" | "confirmed_source" | "watch" | "basket">;
    alternatives: ContinuityAlternative[];
  }>;
  without_alternative: number;
}

export interface ContinuityReport {
  stale_days: number;
  scope: "relied" | "all";
  suppliers: ContinuityEntry[];
}

export interface ShippingRuleInput {
  flat_fee_minor: MinorUnits;
  free_threshold_minor: MinorUnits | null;
  dg_fee_minor: MinorUnits;
  currency: string;
  duty_fraction: DecimalString;
  brokerage_minor: MinorUnits;
  notes: string;
}

export interface QueuedJob {
  id: Uuid;
  kind: string;
  status: string;
}

export const suppliersKeys = {
  all: ["suppliers"] as const,
  list: (params: SupplierListParams) => ["suppliers", "list", params] as const,
  detail: (ref: string) => ["suppliers", "detail", ref] as const,
  products: (id: Uuid, params: ProductListParams) => ["suppliers", "products", id, params] as const,
  history: (variantId: Uuid) => ["suppliers", "history", variantId] as const,
  matches: (params: MatchListParams) => ["suppliers", "matches", params] as const,
  continuity: () => ["suppliers", "continuity"] as const,
};

export function useSuppliers(params: SupplierListParams) {
  return useQuery({
    queryKey: suppliersKeys.list(params),
    queryFn: ({ signal }) => apiGet<Paged<Supplier>>("/suppliers/", { params: { limit: 500, ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function supplierQuery(ref: string) {
  return queryOptions({
    queryKey: suppliersKeys.detail(ref),
    queryFn: ({ signal }) => apiGet<SupplierDetail>(`/suppliers/${encodeURIComponent(ref)}`, { signal }),
  });
}

export function useSupplier(ref: string) {
  return useQuery(supplierQuery(ref));
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: Uuid; sync_enabled?: boolean; status?: SupplierStatus; notes?: string }) =>
      apiPatch<SupplierDetail>(`/suppliers/${id}`, body),
    onMutate: async ({ id, sync_enabled }) => {
      if (sync_enabled === undefined) return undefined;
      await queryClient.cancelQueries({ queryKey: ["suppliers", "list"] });
      const snapshots = queryClient.getQueriesData<Paged<Supplier>>({ queryKey: ["suppliers", "list"] });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        queryClient.setQueryData<Paged<Supplier>>(key, {
          ...data,
          items: data.items.map((item) => (item.id === id ? { ...item, sync_enabled } : item)),
        });
      }
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.snapshots ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useSyncSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiPost<QueuedJob>(`/suppliers/${id}/sync`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useSupplierProducts(id: Uuid | undefined, params: ProductListParams) {
  return useQuery({
    queryKey: suppliersKeys.products(id ?? "", params),
    queryFn: ({ signal }) => apiGet<Paged<Product>>(`/suppliers/${id}/products`, { params: { ...params }, signal }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function searchProducts(supplierId: Uuid, q: string, signal?: AbortSignal): Promise<Product[]> {
  return apiGet<Paged<Product>>(`/suppliers/${supplierId}/products`, { params: { q, limit: 20 }, signal }).then(
    (page) => page.items,
  );
}

export function useVariantHistory(variantId: Uuid | null) {
  return useQuery({
    queryKey: suppliersKeys.history(variantId ?? ""),
    queryFn: ({ signal }) => apiGet<VariantHistory>(`/suppliers/variants/${variantId}/history`, { signal }),
    enabled: Boolean(variantId),
  });
}

export function useMatches(params: MatchListParams, enabled = true) {
  return useQuery({
    queryKey: suppliersKeys.matches(params),
    queryFn: ({ signal }) => apiGet<Paged<ListingMatch>>("/suppliers/matches", { params: { ...params }, signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useDecideMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: Uuid; decision: "accepted" | "rejected" }) =>
      apiPost<ListingMatch>(`/suppliers/matches/${id}/decide`, { decision }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useManualMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { product_id: Uuid; material_id: Uuid }) => apiPost<ListingMatch>("/suppliers/matches", body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useSaveShippingRule(supplierId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, values }: { ruleId: Uuid | null; values: ShippingRuleInput }) =>
      ruleId
        ? apiPatch<ShippingRule>(`/suppliers/shipping-rules/${ruleId}`, values)
        : apiPost<ShippingRule>("/suppliers/shipping-rules/", { supplier_id: supplierId, ...values }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useDeleteShippingRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: Uuid) => apiDelete(`/suppliers/shipping-rules/${ruleId}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: suppliersKeys.all }),
  });
}

export function useContinuity() {
  return useQuery({
    queryKey: suppliersKeys.continuity(),
    queryFn: ({ signal }) => apiGet<ContinuityReport>("/suppliers/continuity", { signal }),
    staleTime: 5 * 60_000,
  });
}

/** "15 g" and "10 % in DPG" for a pack; dilution is null for neat packs. */
export function packText(pack: {
  pack_qty?: DecimalString | null;
  pack_unit?: string | null;
  pack_g?: DecimalString | null;
  dilution_fraction?: DecimalString | null;
  solvent_text?: string;
}): { size: string | null; dilution: string | null } {
  let size: string | null = null;
  if (pack.pack_unit === "ml" && pack.pack_qty) size = `${formatNumber(pack.pack_qty, { maxDecimals: 2 })}\u00a0mL`;
  else if (pack.pack_unit === "oz" && pack.pack_qty)
    size = `${formatNumber(pack.pack_qty, { maxDecimals: 2 })}\u00a0oz`;
  else if (pack.pack_g) size = `${formatNumber(pack.pack_g, { maxDecimals: 3 })}\u00a0g`;
  const dilution = pack.dilution_fraction
    ? `${formatPercentNumber(pack.dilution_fraction, { from: "fraction", minDecimals: 0, maxDecimals: 2 })}\u00a0% in ${pack.solvent_text || "solvent"}`
    : null;
  return { size, dilution };
}

const PLATFORM_LABELS: Record<SupplierPlatform, string> = {
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  bigcommerce: "BigCommerce",
  magento: "Magento",
  squarespace: "Squarespace",
  wix: "Wix",
  custom: "Custom site",
  marketplace: "Marketplace",
};

export function platformLabel(platform: SupplierPlatform): string {
  return PLATFORM_LABELS[platform] ?? platform;
}

/** Why a store's catalog can't be synced: its site turns automated clients away (difficulty 5, opted
 * out), it's a marketplace, or no adapter reads it. */
export function noSyncReason(supplier: Pick<Supplier, "platform" | "scraping_difficulty">): string {
  if ((supplier.scraping_difficulty ?? 0) >= 5) return "Its site turns automated clients away";
  if (supplier.platform === "marketplace") return "Marketplaces can only be read through their official APIs";
  return "No catalog adapter for this store yet";
}
